import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SearchService } from '../../shared/services/search.service';
import { Content } from '../../core/content/content';
import { EntityService } from '../../shared/services/entity.service';
import { TerritoryKind } from '../../core/territory-kind/territory-kind.enum';
import { ArticleImSearchService } from '../../shared/services/article-im-search.service';
import { Subscription } from 'rxjs';
import { PublicationSource } from '../search/publication-source';
import { SearchMode } from '../search/search-mode';
import { first } from 'rxjs/operators';
import { Article } from '../../models/article';
import { ApiService } from '../../shared/services/api/api.service';
import { LoaderService } from '../../common-explain/services/loader.service';
import { TerritoryCountersService } from '../../shared/services/territory-counters.service';
import { SmartSearchService } from '../../shared/services/smart-search.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FeatureModalSmartSearchComponent } from '../feature-modal-smart-search/feature-modal-smart-search.component';

@Component({
  selector: 'app-articles-page',
  templateUrl: './articles-page.component.html',
  styleUrls: ['./articles-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ArticlesPageComponent extends Content implements OnInit, OnDestroy {
  @Input() impacterId: string | null = null;
  @ViewChild('panel', { read: ElementRef }) panel?: ElementRef;
  nbTotalArticle!: number;
  nbTotalSource!: number;
  articlesCount!: number;
  sourcesCount!: number;
  impacterArticlesCount = 0;
  sourcesList!: string[];
  docId: string | null = null;
  sources!: Array<string>;
  firstInit = true;

  /** Subscriptions to services */
  getTerritoryCountersSubscription!: Subscription;
  getEntityIdSubscription!: Subscription;
  entityServiceSubscription!: Subscription;

  constructor(
    private apiService: ApiService,
    protected override searchService: SearchService,
    protected override entityService: EntityService,
    protected override articleImSearchService: ArticleImSearchService,
    private loaderService: LoaderService,
    private territoryCountersService: TerritoryCountersService,
    protected smartSearchService: SmartSearchService,
    protected modalService: NgbModal
  ) {
    super(searchService, entityService, articleImSearchService);
  }

  ngOnInit() {
    this.apiService.exStatistics.ExplainStatisticsReplaySubject.pipe(first()).subscribe(statistics => {
      this.nbTotalArticle = statistics.allArticlesCount;
      this.nbTotalSource = statistics.allSourcesCount;
    });

    this.getTerritoryCountersSubscription = this.territoryCountersService.getTerritoryCounters().subscribe(territoryCounters => {
      this.sources = territoryCounters.sourcesTerritories.map(source => source.name);
    });

    this.getEntityIdSubscription = this.entityService.getEntityId().subscribe(docId => {
      this.docId = docId;
    });

    this.entityServiceSubscription = this.entityService.BookMarkPathsSubject.subscribe(({ id, bookmarkPaths }) => {
      if (this.entities) {
        const updatedBookmarkPathsIndex = this.entities.findIndex((obj: any) => obj['id'] === id);
        this.entities[updatedBookmarkPathsIndex]['bookmarkPaths'] = bookmarkPaths;
      }
    });

    if (!this.impacterId) {
      this.searchService.sentPageIndex(1);
    }
    this.getSearch(!this.impacterId);
  }

  ngOnDestroy() {
    // console.log('destroy de article-page');
    /** Unsubscribe of the unherited searchSubscription from Content */
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    /** Unsubscribe of subscriptions */
    if (this.getEntityIdSubscription) {
      this.getEntityIdSubscription.unsubscribe();
    }
    if (this.getTerritoryCountersSubscription) {
      this.getTerritoryCountersSubscription.unsubscribe();
    }

    if (this.impacterId) {
      this.articleImSearchService.reset();
    } else {
      this.searchService.currentPage = 1;
    }

    if (this.entityServiceSubscription) {
      this.entityServiceSubscription.unsubscribe();
    }
  }

  retrieveData(search: any): void {
    delete search['filters']['territories_kind'];
    search['filters']['source'] = this.sources;
    const body = this.buildBody(search);
    if (this.impacterId) {
      body['impacter_id'] = Number(this.impacterId);
    }
    body['filters']['territories'] = body['filters']['territories'].filter((t: Array<string>) => t.includes(TerritoryKind.COMMUNE));

    let searchMode: SearchMode;
    if (!this.impacterId) {
      searchMode = this.searchService.SearchModeMap.get(PublicationSource.ARTICLES)!;
    } else {
      searchMode = this.articleImSearchService.SearchModeMap.get(PublicationSource.ARTICLES)!;
    }

    this.request(
      this.apiService.article
        .retrieveArticles(body, searchMode)
        .pipe(first())
        .subscribe(
          data => {
            this.panel?.nativeElement.querySelector('.content-list')?.scrollTo({ top: 0, behavior: 'smooth' });
            this.getTerritoryCountersSubscription = this.territoryCountersService.getTerritoryCounters().subscribe(territoryCounters => {
              this.articlesCount = territoryCounters.articlesCount;
              this.sourcesCount = territoryCounters.sourcesCount;
              this.sourcesList = territoryCounters.sourcesTerritories
                .filter(src => src.is_active)
                .map(s => s.name)
                .sort((s1, s2) => s1.localeCompare(s2));
            });
            this.setEntities(data, search['limit'], search['offset']);
            if (this.impacterId) {
              this.entityService.sendArticleIMCurrentCount(this.totalCount);
              if (!search['text'] && this.firstInit) {
                this.entityService.sendArticleIMTotalCount(this.totalCount);
                this.impacterArticlesCount = this.totalCount ?? 0;
                this.firstInit = false;
              }
            }
          },
          _ => {
            this.totalCount = 0;
            this.loaderService.hideLoader(true);
          }
        )
    );
  }

  get articles(): Array<Article> {
    return this.entities as Array<Article>;
  }

  openSmartSearchIntro() {
    this.modalService.open(FeatureModalSmartSearchComponent, { centered: true, windowClass: 'modal-auto' });
  }
}
